/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { GitBranch, Shield, Zap } from 'lucide-react';
import type { VelocitySettings } from '@shared/types/organizationSettings';

type PushToMainStrategy = 'auto-merge' | 'auto-pr' | 'event-triggered' | 'hybrid' | 'manual';

interface VelocityBranchSectionProps {
  settings: VelocitySettings;
  onChange: (settings: VelocitySettings) => void;
  onDirty: () => void;
}

export function VelocityBranchSection({ 
  settings, 
  onChange, 
  onDirty 
}: VelocityBranchSectionProps) {
  const simpleMode = settings.simpleMode ?? true;
  
  const handleSimpleModeChange = (enabled: boolean) => {
    onChange({
      ...settings,
      simpleMode: enabled,
      sync: {
        ...settings.sync,
        autoSave: {
          enabled: !enabled,
          commitOnChange: !enabled,
          pushAfterCommit: !enabled,
        }
      }
    });
    onDirty();
  };
  
  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <GitBranch size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Velocity Branch Settings</h3>
      </div>
      
      {/* Simple Mode Toggle - Primary Setting */}
      <div style={{ 
        marginBottom: '24px',
        padding: '16px',
        background: simpleMode ? '#f0fdf4' : '#eff6ff',
        borderRadius: '8px',
        border: `1px solid ${simpleMode ? '#bbf7d0' : '#bfdbfe'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ 
            padding: '8px',
            background: simpleMode ? '#dcfce7' : '#dbeafe',
            borderRadius: '8px'
          }}>
            {simpleMode ? (
              <Shield size={24} style={{ color: '#16a34a' }} />
            ) : (
              <Zap size={24} style={{ color: '#2563eb' }} />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                {simpleMode ? 'Simple Mode' : 'Auto-Save Mode'}
              </h4>
              <button
                onClick={() => handleSimpleModeChange(!simpleMode)}
                style={{
                  position: 'relative',
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  background: simpleMode ? '#22c55e' : '#3b82f6',
                  transition: 'background 0.2s ease',
                  flexShrink: 0,
                }}
                aria-label={`Switch to ${simpleMode ? 'Auto-Save' : 'Simple'} Mode`}
                role="switch"
                aria-checked={simpleMode}
              >
                <span style={{
                  position: 'absolute',
                  top: '3px',
                  left: simpleMode ? '25px' : '3px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s ease',
                }} />
              </button>
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
              {simpleMode ? (
                <>
                  <strong>Manual source control.</strong> You initiate all git actions — no auto-save, auto-commit, or auto-push. 
                  Changes are shown in the Changes page where you can commit and push when ready.
                </>
              ) : (
                <>
                  <strong>Google Docs-like experience.</strong> Changes are automatically committed and pushed to the remote velocity branch. 
                  No manual git operations needed.
                </>
              )}
            </p>
            <div style={{ 
              marginTop: '12px', 
              padding: '8px 12px', 
              background: simpleMode ? '#dcfce7' : '#dbeafe',
              borderRadius: '4px',
              fontSize: '13px',
              color: simpleMode ? '#166534' : '#1e40af'
            }}>
              {simpleMode ? (
                <>✓ Commit button · ✓ Push button · ✓ Pull button · No background operations</>
              ) : (
                <>✓ Auto-commit · ✓ Auto-push · ✓ Background sync · No manual steps</>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Sync Mode Selection */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>
          Sync Target
        </h4>
        
        <div className="form-group">
          <label htmlFor="sync-mode">Which branch to sync with</label>
          <select
            id="sync-mode"
            value={settings.sync?.syncMode || 'velocity-mode'}
            onChange={(e) => {
              onChange({
                ...settings,
                sync: { ...settings.sync, syncMode: e.target.value as 'velocity-mode' | 'main-sync' }
              });
              onDirty();
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="velocity-mode">Velocity Branch (origin/velocity) - Recommended</option>
            <option value="main-sync">Main Branch (sync with main/master)</option>
          </select>
          <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            {settings.sync?.syncMode === 'main-sync' 
              ? 'Changes are synced with the main branch, requiring PRs for protected branches.' 
              : 'Changes are synced with the dedicated velocity branch, bypassing main branch protections.'}
          </small>
        </div>
      </div>
      
      {/* Sync Settings - shown based on mode */}
      {settings.sync?.syncMode === 'main-sync' && (
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>
          Sync Settings (Main → Velocity)
        </h4>
        
        <div className="form-group">
          <label htmlFor="sync-interval">Auto-Sync Interval</label>
          <select
            id="sync-interval"
            value={settings.sync?.autoSyncInterval || 10}
            onChange={(e) => {
              onChange({
                ...settings,
                sync: { ...settings.sync, autoSyncInterval: parseInt(e.target.value) }
              });
              onDirty();
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value={5}>Every 5 minutes</option>
            <option value={10}>Every 10 minutes (recommended)</option>
            <option value={15}>Every 15 minutes</option>
            <option value={30}>Every 30 minutes</option>
            <option value={60}>Every hour</option>
          </select>
          <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            How often to sync the velocity branch with the main branch
          </small>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.sync?.enableSemanticMerge ?? true}
              onChange={(e) => {
                onChange({
                  ...settings,
                  sync: { ...settings.sync, enableSemanticMerge: e.target.checked }
                });
                onDirty();
              }}
            />
            <span style={{ fontSize: '14px' }}>Enable semantic merge for markdown files</span>
          </label>
        </div>
      </div>
      )}
      
      {/* Push to Main Strategy - only shown in main-sync mode */}
      {settings.sync?.syncMode === 'main-sync' && (
      <div>
        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>
          Push to Main Strategy (Velocity → Main)
        </h4>
        
        <div className="form-group">
          <label htmlFor="push-strategy">Strategy</label>
          <select
            id="push-strategy"
            value={settings.pushToMain?.strategy || 'hybrid'}
            onChange={(e) => {
              onChange({
                ...settings,
                pushToMain: { ...settings.pushToMain, strategy: e.target.value as PushToMainStrategy }
              });
              onDirty();
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="hybrid">Hybrid (recommended) - Auto-merge if safe, PR otherwise</option>
            <option value="auto-merge">Auto-Merge - Push directly to main</option>
            <option value="auto-pr">Auto-PR - Create PRs on schedule</option>
            <option value="event-triggered">Event-Triggered - PR on release/sprint events</option>
            <option value="manual">Manual - User clicks "Push to Main"</option>
          </select>
        </div>
        
        {/* Hybrid Strategy Options */}
        {settings.pushToMain?.strategy === 'hybrid' && (
          <div style={{ 
            marginTop: '16px', 
            padding: '16px', 
            background: '#fff',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <h5 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', color: '#4b5563' }}>
              Hybrid Strategy Options
            </h5>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.pushToMain?.hybrid?.autoMergeIfSafe ?? true}
                  onChange={(e) => {
                    const currentHybrid = settings.pushToMain?.hybrid;
                    onChange({
                      ...settings,
                      pushToMain: {
                        ...settings.pushToMain,
                        hybrid: { 
                          autoMergeIfSafe: e.target.checked,
                          fallbackToPR: currentHybrid?.fallbackToPR ?? true,
                          maxChangesForAutoMerge: currentHybrid?.maxChangesForAutoMerge ?? 50,
                        }
                      }
                    });
                    onDirty();
                  }}
                />
                <span style={{ fontSize: '14px' }}>Auto-merge if only product data changed</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.pushToMain?.hybrid?.fallbackToPR ?? true}
                  onChange={(e) => {
                    const currentHybrid = settings.pushToMain?.hybrid;
                    onChange({
                      ...settings,
                      pushToMain: {
                        ...settings.pushToMain,
                        hybrid: { 
                          autoMergeIfSafe: currentHybrid?.autoMergeIfSafe ?? true,
                          fallbackToPR: e.target.checked,
                          maxChangesForAutoMerge: currentHybrid?.maxChangesForAutoMerge ?? 50,
                        }
                      }
                    });
                    onDirty();
                  }}
                />
                <span style={{ fontSize: '14px' }}>Create PR if merge conflicts exist</span>
              </label>
              
              <div className="form-group" style={{ marginTop: '8px', marginBottom: 0 }}>
                <label htmlFor="max-changes">Max changes for auto-merge</label>
                <input
                  id="max-changes"
                  type="number"
                  min={1}
                  max={500}
                  value={settings.pushToMain?.hybrid?.maxChangesForAutoMerge ?? 50}
                  onChange={(e) => {
                    const currentHybrid = settings.pushToMain?.hybrid;
                    onChange({
                      ...settings,
                      pushToMain: {
                        ...settings.pushToMain,
                        hybrid: { 
                          autoMergeIfSafe: currentHybrid?.autoMergeIfSafe ?? true,
                          fallbackToPR: currentHybrid?.fallbackToPR ?? true,
                          maxChangesForAutoMerge: parseInt(e.target.value),
                        }
                      }
                    });
                    onDirty();
                  }}
                  style={{
                    width: '120px',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Create PR if more than this many changes
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
      
      {/* Auto-Save Settings - only shown in velocity-mode when Simple Mode is disabled */}
      {settings.sync?.syncMode !== 'main-sync' && !simpleMode && (
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>
          Auto-Save Settings
        </h4>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.sync?.autoSave?.enabled ?? false}
              onChange={(e) => {
                onChange({
                  ...settings,
                  sync: { 
                    ...settings.sync, 
                    autoSave: { 
                      enabled: e.target.checked,
                      commitOnChange: settings.sync?.autoSave?.commitOnChange ?? true,
                      pushAfterCommit: settings.sync?.autoSave?.pushAfterCommit ?? true
                    } 
                  }
                });
                onDirty();
              }}
            />
            <span style={{ fontSize: '14px' }}>Enable automatic saving</span>
          </label>
          <small style={{ color: '#666', fontSize: '12px', marginLeft: '24px', marginTop: '-8px' }}>
            Automatically save changes to the remote velocity branch
          </small>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.sync?.autoSave?.commitOnChange ?? true}
              disabled={!settings.sync?.autoSave?.enabled}
              onChange={(e) => {
                onChange({
                  ...settings,
                  sync: { 
                    ...settings.sync, 
                    autoSave: { 
                      enabled: settings.sync?.autoSave?.enabled ?? true,
                      commitOnChange: e.target.checked,
                      pushAfterCommit: settings.sync?.autoSave?.pushAfterCommit ?? true
                    } 
                  }
                });
                onDirty();
              }}
            />
            <span style={{ fontSize: '14px' }}>Auto-commit on change</span>
          </label>
          <small style={{ color: '#666', fontSize: '12px', marginLeft: '24px', marginTop: '-8px' }}>
            Automatically create commits when files change
          </small>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.sync?.autoSave?.pushAfterCommit ?? true}
              disabled={!settings.sync?.autoSave?.enabled}
              onChange={(e) => {
                onChange({
                  ...settings,
                  sync: { 
                    ...settings.sync, 
                    autoSave: { 
                      enabled: settings.sync?.autoSave?.enabled ?? true,
                      commitOnChange: settings.sync?.autoSave?.commitOnChange ?? true,
                      pushAfterCommit: e.target.checked
                    } 
                  }
                });
                onDirty();
              }}
            />
            <span style={{ fontSize: '14px' }}>Push to remote after commit</span>
          </label>
          <small style={{ color: '#666', fontSize: '12px', marginLeft: '24px', marginTop: '-8px' }}>
            Sync commits to the remote velocity branch immediately
          </small>
        </div>
      </div>
      )}
      
      {/* Simple Mode Info - shown in velocity-mode when Simple Mode is enabled */}
      {settings.sync?.syncMode !== 'main-sync' && simpleMode && (
      <div style={{ 
        marginBottom: '24px',
        padding: '16px',
        background: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>
          Source Control in Simple Mode
        </h4>
        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
          In Simple Mode, all git operations are manual. Use the <strong>Changes</strong> page to:
        </p>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#6b7280', lineHeight: '1.8' }}>
          <li>View uncommitted changes</li>
          <li>Commit changes with a custom message</li>
          <li>Push commits to the remote repository</li>
          <li>Pull changes from the remote repository</li>
        </ul>
      </div>
      )}
    </div>
  );
}
